<template>
<div class="quadia-player-app" :class="{'full-height': fullHeight}">
  <poster v-if="isPostermode" v-on:disabled="initAndPlay"></poster>
  <custom-error v-if="showCustomErrorMessage" :message="customErrorMessage"></custom-error>
  <video-js-video v-if="loaded"></video-js-video>
</div>
</template>

<script>
import Video from './models/Video';
import VideoClient from './adapters/VideoClient';
import { EventBus } from './services/EventBus';
import { GoogleAnalytics } from './services/GoogleAnalytics';
import { AdobeAnalytics } from './services/AdobeAnalytics';
import { PlayerManager } from './services/PlayerManager';
import { ConfigManager } from './services/ConfigManager';
import { externalEventProcesser } from './services/ExternalEventProcesser';
import { mapState, mapGetters, mapMutations } from 'vuex'
import CloneDeep from 'lodash/cloneDeep'
import CustomError from '@/components/CustomError';
import {AdobeAnalyticsByURL} from '@/services/AdobeAnalyticsByURL';
import { timer } from './services/Timer';

export default {
  name: 'App',
  components: {
    CustomError,
    Poster: () => import(/* webpackChunkName: "poster" */'./components/Poster'),
    VideoJsVideo: () => import(/* webpackChunkName: "videoVideoJs" */'./components/VideoVideoJs'),
  },
  data() {
    return {
      loaded: false,
      original: {
        interactiveItems: [],
        slides: []
      },
      fullHeight: false,
      customErrorMessage: '',
      showCustomErrorMessage: false
    };
  },
  computed: {
    ...mapState([
      'video',
      'config',
      'dataset',
      'time'
    ]),
    ...mapGetters([
      'isPostermode',
      'isInteractive',
      'hasInteractiveControls',
    ]),
  },
  methods: {
    ...mapMutations([
      'closeTheaterMode', 'setMute', 'setInteractiveItems', 'setSlides',
      'setActiveMode', 'setVideo', 'changeConfig', 'changeTime'
    ]),
    loadVideo() {
      return this.config.videoId
        ? this.loadOndemandVideo()
        : this.makeVideoByUrl()
    },
    makeVideoByUrl() {
      let video = new Video({
        sourceURL: this.config.videoUrl
      })
      this.setVideo(video);
      return Promise.resolve()
    },
    async loadOndemandVideo() {
      var client = new VideoClient()
      const result = await client.get();
      this.original = {
        slides: CloneDeep(result.slides),
        interactiveItems: CloneDeep(result.interactiveItems),
        presentationMode: result.presentationMode || 'PIP'
      }

      this.setVideo(result.video);
      this.setInteractiveItems(result.interactiveItems);
      this.setSlides(result.slides);
      this.setActiveMode(result.presentationMode);
    },
    on(event, callback) {
      if (['timeChange', 'playerReady', 'firstFrame', 'play', 'pause', 'seek', 'complete', 'theaterMode'].indexOf(event) === -1) {
        throw Error('Unsupported event');
      }
      EventBus.$on(event, callback);
    },
    async emit(event, data) {
      var id, chapter;
      let presentation;
      switch (event) {
        case 'play':
          PlayerManager.play()
          break;
        case 'pause':
          PlayerManager.pause();
          break;
        case 'seek':
          PlayerManager.seek(data);
          break;
        case 'stop':
          PlayerManager.stop();
          break;
        case 'setPresentationMode':
        case 'setPresentationPrimaryWindow':
        case 'setPresentationZoom':
        case 'setPresentationSlidePosition':
        case 'forceFullscreen':
          EventBus.$emit(event, data);
          break;
        case 'updateConfig':
          this.reinitialize(data)
          break;
        case 'externalFullscreen':
          EventBus.$emit(event, {...data});
          break;
        case 'updatePresentation':
          presentation = await externalEventProcesser.processPresentation(data);
          EventBus.$emit('setPresentationMode', presentation.presentationMode);
          this.setSlides(presentation.items);
          break;
        case 'nextchapter':
          id = this.video.chapters.getNextChapterIdByOffset(PlayerManager.getPosition());
          chapter = this.video.chapters.getById(id)
          if (chapter) {
            PlayerManager.seek(chapter.offset)
          }
          break;
        case 'previouschapter':
          id = this.video.chapters.getNextChapterIdByOffset(PlayerManager.getPosition());
          id -= 2
          chapter = this.video.chapters.getById(id)
          if (chapter) {
            PlayerManager.seek(chapter.offset)
          }
          break;
        case 'gotochapter':
          chapter = this.video.chapters.getById(data)
          if (chapter) {
            PlayerManager.seek(chapter.offset)
          }
          break;
        case 'setCaptionsStyle':
          PlayerManager.player.setCaptions(data)
          break;
        case 'closeTheaterMode':
          this.closeTheaterMode();
          break;
        default:
          throw new Error('Unsupported event emitted: ' + name)
      }
    },
    async load() {
      await this.loadVideo();
      EventBus.$once('playerReady', () => {
        PlayerManager.seek(data.startAt)
        if (data.autoplay) {
          PlayerManager.play();
        }
      })
      this.loaded = true;
    },
    updatePlaylist(data) {
      let updatedConfig = ConfigManager.updateConfig(this.config, data)
      this.changeConfig(updatedConfig);
      this.loaded = false;
      timer.stop();
      this.changeTime(data.startAt);
      this.load();
    },
    reinitialize(data) {
      let updatedConfig = ConfigManager.updateConfig(this.config, data)
      this.changeConfig(updatedConfig);
      if (data.videoId || data.videoUrl) {
        this.loaded = false
        this.load();
      }
    },
    get(attribute) {
      switch (attribute) {
        case 'duration':
          return PlayerManager.duration();
        case 'state':
          return PlayerManager.player.getState();
        case 'time':
          return this.time;
        case 'chapters':
          return this.video.chapters.getChapters();
        case 'meta':
          return this.video.metaData;
        default:
          throw new Error('Unsupported get parameter: ' + event)
      }
    },
    async init() {
      EventBus.$on('reinitialize', this.reinitialize)
      EventBus.$on('updatePlaylist', this.updatePlaylist)
      try {
        await this.loadVideo();
        this.loaded = true
        if (this.config.autoplay === 'true') {
          this.setMute(true)
        }
      } catch(error) {
        this.showCustomErrorMessage = true;
        if (error.response && error.response.data.error) {
          this.customErrorMessage = error.response.data.error;
        } else {
          console.error(error);
          this.customErrorMessage = 'The video could not be loaded';
        }
      }

      GoogleAnalytics.loadIfConfigured();
      AdobeAnalytics.loadIfConfigured();
      AdobeAnalyticsByURL.loadIfConfigured();
    },
    initAndPlay() {
      EventBus.$once('playerReady', () => {
        this.emit('play')
      })
      this.init()
    }
  },
  created() {
    if (!this.isPostermode) {
      this.init()
    }
  },
  mounted() {
    if (this.config.height && !this.config.audioOnly) {
      document.getElementsByTagName('html')[0].style.height = '100%';
      document.body.style.height = '100%';
      this.fullHeight = true;
    }
    if (this.config.audioOnly) {
      EventBus.$emit('heightChange', this.config.height);
    } else {
      const width = document.body.getBoundingClientRect().width;
      EventBus.$emit('heightChange', width / 16 * 9);
    }
    if (this.config.lang) {
      document.documentElement.setAttribute('lang', this.config.lang);
    }
  }
};
</script>

<style lang='scss'>
html {
  overflow: hidden;
}

body {
  background-color: black;
  overflow: hidden;
}

.quadia-player-app {
  &.full-height {
    height: 100%;
    .wrapper {
      height: 100%;
    }
  }

  .error-context-copy {
    padding: 5px 0;
    cursor: pointer;
  }
}
</style>
